import type { MouseEventHandler } from 'react';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  AppAudioPlayerControlsDescription,
  AppAudioPlayerControlsPauseFilled,
  AppAudioPlayerControlsPlayFilled,
  AppAudioPlayerEqualizer
} from '@elfsight/icons';
import {
  getColorWithAlpha,
  getHandleActionKeyDown
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useInView } from 'react-intersection-observer';
import type { SourceType } from '../../../../types';
import {
  getAnimation,
  hmsToSecondsOnly,
  timeFormat,
  WHITE
} from '../../../helpers';
import ReactPlayer from './../../../player';
import { Description, Image, TrackInfo, useTheme } from '../../common';
import type { ExtendedAudioItem } from '../../sources';
import { useSource } from '../../sources';
import { usePlayer } from '../PlayerProvider';

interface PlaylistItemProps {
  data: ExtendedAudioItem;
  id: number;
}

export const PlaylistItem = ({ data, id }: PlaylistItemProps) => {
  const [ref] = useInView();
  const { textColor } = useTheme();
  const {
    trackElements = [],
    sourceType,
    coverImage,
    accentColor,
    messageDescriptionButton,
    shouldCollectMetadata
  } = useSettings();
  const {
    setCurrentItemId,
    currentItemId,
    currentItemDescriptionId,
    setCurrentItemDescriptionId
  } = useSource();
  const {
    setDuration,
    setProgress,
    playing,
    togglePlaying,
    showPlaylist,
    hasInteracted
  } = usePlayer();
  const { title, link, author, image, duration, description, hasCaptionValue } =
    data;

  const isPlayer = sourceType === 'player';

  const [currentItemDuration, setCurrentItemDuration] = useState(
    isPlayer ? duration : 0
  );

  useEffect(() => {
    if (isPlayer) {
      setCurrentItemDuration(duration);
    }
  }, [duration, isPlayer]);

  const handleClick: MouseEventHandler = (event) => {
    event.stopPropagation();
    if (id !== currentItemDescriptionId) {
      setCurrentItemDescriptionId(id);
    } else {
      setCurrentItemDescriptionId(null);
    }
  };

  const isCurrentTrack = id === currentItemId;

  const withAuthor = trackElements.includes('author') && !!author;
  const withImage = trackElements.includes('image');
  const withDescription =
    trackElements.includes('description') && !!description;
  const withDuration = trackElements.includes('duration');
  const withCaption =
    trackElements.includes('caption') &&
    hasCaptionValue &&
    !!shouldCollectMetadata;

  const track: ExtendedAudioItem = {
    ...data,
    hasTitle: true,
    hasAuthor: withAuthor,
    hasCaption: withCaption
  };

  const iconFill = withImage ? WHITE : textColor;

  const { url } = coverImage ?? {};

  const handleItemClick = () => {
    if (!hasInteracted && playing) {
      return;
    }

    if (!isCurrentTrack) {
      setDuration(0);
      setProgress({});
      setCurrentItemId(id);
      togglePlaying(true);
    } else {
      togglePlaying(!playing);
    }
  };

  const formattedDuration = (() => {
    if (!currentItemDuration) {
      const derivedDuration =
        typeof duration === 'string' ? hmsToSecondsOnly(duration) : duration;
      return timeFormat(derivedDuration) ?? '';
    }

    return timeFormat(Number(currentItemDuration));
  })();

  return (
    <PlaylistItemComponent
      ref={ref}
      tabIndex={showPlaylist ? 0 : -1}
      onClick={handleItemClick}
      onKeyDown={getHandleActionKeyDown(handleItemClick)}
      _sourceType={sourceType}
      _textColor={textColor}
      _withImage={withImage}
      _withAuthor={withAuthor}
      _isActive={isCurrentTrack}
    >
      <PlaylistItemInfoContainer>
        {withImage && (
          <ImageContainer trackName={title} link={image || url}>
            <IconContainer
              style={{
                opacity: isCurrentTrack ? 1 : undefined,
                backgroundColor: !withImage ? 'transparent' : undefined
              }}
            >
              {playing && isCurrentTrack && (
                <Icon as={IconPlaying} fill={iconFill} />
              )}

              {playing && isCurrentTrack && (
                <Icon as={IconPause} fill={iconFill} />
              )}

              {(currentItemId !== id || (isCurrentTrack && !playing)) && (
                <Icon as={AppAudioPlayerControlsPlayFilled} fill={iconFill} />
              )}
            </IconContainer>
          </ImageContainer>
        )}

        <Info>
          <TrackInfo track={track} disableTicker />
        </Info>

        {(withDescription || withDuration) && (
          <AdditionalInfoContainer>
            {withDescription && (
              <DescriptionIconContainer
                title={messageDescriptionButton}
                onClick={handleClick}
              >
                <DescriptionIcon
                  as={AppAudioPlayerControlsDescription}
                  style={{
                    fill:
                      id === currentItemDescriptionId ? accentColor : textColor,
                    opacity: id === currentItemDescriptionId ? '0.7' : '0.5'
                  }}
                />
              </DescriptionIconContainer>
            )}

            {withDuration && <Duration>{formattedDuration}</Duration>}
          </AdditionalInfoContainer>
        )}

        {withDuration && !currentItemDuration && isPlayer && (
          <ReactPlayer
            key={link}
            url={link}
            muted
            onDuration={setCurrentItemDuration}
            config={{
              file: {
                attributes: {
                  preload: 'auto'
                },
                forceAudio: true
              }
            }}
          />
        )}
      </PlaylistItemInfoContainer>

      {id === currentItemDescriptionId && (
        <Description
          text={description}
          shortenedDescription={false}
          _marginTop={6}
        />
      )}
    </PlaylistItemComponent>
  );
};

const ImageContainer = styled(Image)`
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
`;

const IconContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(17, 17, 17, 0.5);
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: ${getAnimation()};
`;

const Icon = styled.svg`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  transition: ${getAnimation()};
`;

const IconPlaying = styled(AppAudioPlayerEqualizer)`
  width: 48px;
  height: 48px;
  opacity: 1;
`;

const IconPause = styled(AppAudioPlayerControlsPauseFilled)`
  opacity: 0;
`;

const DescriptionIconContainer = styled.div`
  margin-right: 5px;
  width: 20px;
`;

const DescriptionIcon = styled.svg`
  width: 20px;
  height: 20px;
  transition: ${getAnimation()};

  &:hover {
    opacity: 1 !important;
  }
`;

const Info = styled.div`
  line-height: 1;
  width: 100%;
  display: flex;
  overflow: hidden;
  align-items: center;
`;

export const Duration = styled.div`
  font-size: 13px;
  line-height: 1.2;
  text-align: right;
  padding-left: 0;
  opacity: 0.7;
  width: 50px;
`;

const AdditionalInfoContainer = styled.div`
  max-width: 130px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const activeTrackMixin = css<{
  _textColor: string;
}>`
  background-color: ${({ _textColor }) =>
    getColorWithAlpha(_textColor || WHITE, 0.05)};
  ${IconContainer} {
    opacity: 1;
  }
  ${IconPause} {
    opacity: 1;
  }
  ${IconPlaying} {
    opacity: 0;
  }
`;

export const PlaylistItemComponent = styled.div<{
  _withAuthor: boolean;
  _withImage: boolean;
  _sourceType: SourceType;
  _textColor: string;
  _isActive: boolean;
}>`
  padding: 6px;
  border-radius: 6px;
  display: flex;
  flex-wrap: wrap;
  cursor: pointer;
  align-items: center;
  box-sizing: border-box;
  height: auto;
  min-height: 60px;
  transition:
    ${getAnimation('opacity', 'ease', 100)},
    ${getAnimation('background-color', 'ease', 200)};

  &:last-child {
    margin-bottom: 16px;
  }

  &:hover {
    ${activeTrackMixin};
  }

  ${({ _isActive }) => _isActive && activeTrackMixin}
`;

const PlaylistItemInfoContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 12px;
`;
